import React, { useEffect, useState } from "react";
import { Canvas } from "@react-three/fiber";
import { Toaster, toast } from "sonner";
import Scene from "../three/Scene";
import HUD from "../hud/HUD";
import Guestbook from "../hud/Guestbook";
import Landing from "./Landing";
import { xrStore } from "../three/xrStore";
import { useStore } from "../store";
import { useAmbient } from "../hooks/useAmbient";
import { getWeather } from "../lib/api";

function AutoWeather() {
  const weather = useStore((s) => s.weather);
  useEffect(() => {
    if (weather !== "auto") return;
    const fetchFor = async (lat, lon, labelPrefix) => {
      try {
        const data = await getWeather(lat, lon);
        const s = useStore.getState();
        let tod = s.timeOfDay;
        if (data.local_time) {
          const d = new Date(data.local_time);
          tod = d.getHours() + d.getMinutes() / 60;
        } else {
          tod = data.is_day ? 13 : 1;
        }
        s.setAuto({ autoWeather: data.weather, timeOfDay: tod, autoLocation: [lat, lon] });
        toast.success(`${labelPrefix}: ${data.weather}, ${Math.round(data.temperature)}°C`, { id: "auto" });
      } catch {
        toast.error("Weather service unavailable", { id: "auto" });
      }
    };

    toast.loading("Syncing to your local sky…", { id: "auto" });
    if (!navigator.geolocation) {
      fetchFor(40.7127, -74.0134, "WTC (default)");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => fetchFor(pos.coords.latitude, pos.coords.longitude, "Live"),
      () => fetchFor(40.7127, -74.0134, "WTC (default)"),
      { timeout: 10000 }
    );
  }, [weather]);
  return null;
}

export default function Experience() {
  const [vrSupported, setVrSupported] = useState(false);
  const [entered, setEntered] = useState(false);
  const setVrActive = useStore((s) => s.setVrActive);
  useAmbient();

  useEffect(() => {
    navigator.xr?.isSessionSupported?.("immersive-vr").then(setVrSupported).catch(() => {});
    const unsub = xrStore.subscribe((s) => setVrActive(!!s.session));
    return () => unsub();
  }, [setVrActive]);

  return (
    <div className="fixed inset-0 bg-[#0b0c10]">
      <Canvas
        shadows
        dpr={[1, 1.6]}
        gl={{ antialias: true, powerPreference: "high-performance" }}
        camera={{ position: [440, 360, 560], fov: 50, near: 1, far: 20000 }}
        onCreated={({ gl, invalidate }) => {
          const canvas = gl.domElement;
          const onLost = (e) => {
            e.preventDefault(); // allow the context to be restored instead of staying blank
          };
          const onRestored = () => {
            invalidate();
          };
          canvas.addEventListener("webglcontextlost", onLost, false);
          canvas.addEventListener("webglcontextrestored", onRestored, false);
        }}
      >
        <Scene />
      </Canvas>

      <AutoWeather />
      <HUD onEnterVR={() => xrStore.enterVR()} vrSupported={vrSupported} />
      <Guestbook />
      <Toaster position="top-center" theme="dark" toastOptions={{ style: { fontFamily: "Manrope" } }} />

      <div className="intro-splash fixed inset-0 z-[60] grid place-items-center bg-[#0b0c10]">
        <div className="text-center">
          <div className="font-serif-display text-[#D4AF37] text-5xl sm:text-7xl font-light tracking-wide">
            2001 — Present
          </div>
          <div className="font-mono-tele text-[10px] tracking-[0.4em] text-stone-400 mt-4 uppercase">
            Reconstructing the World Trade Center
          </div>
          <div className="mt-8 h-px w-56 mx-auto bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
        </div>
      </div>

      {!entered && <Landing onEnter={() => setEntered(true)} />}
    </div>
  );
}
